/**
 * FileListView — Traditional table view of filesystem entries
 *
 * Sortable columns: Name, Size, Type, Modified.
 * Checkbox selection, click folder to navigate.
 *
 * @module @humanizer/studio/components/import/FileListView
 */

import React, { useMemo, useState } from 'react';
import type { DirEntryWithStats } from '../../types/electron';

interface FileListViewProps {
  entries: DirEntryWithStats[];
  dirSizes: Map<string, number>;
  selectedPaths: Set<string>;
  onNavigate: (path: string) => void;
  onToggleSelect: (path: string) => void;
}

type SortKey = 'name' | 'size' | 'type' | 'mtime';
type SortDir = 'asc' | 'desc';

function formatSize(bytes: number): string {
  if (bytes === 0) return '\u2014';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  return `${(bytes / (1024 * 1024 * 1024)).toFixed(1)} GB`;
}

function getExtension(name: string): string {
  if (name.includes('.')) {
    return name.split('.').pop()?.toLowerCase() || '';
  }
  return '';
}

export function FileListView({
  entries,
  dirSizes,
  selectedPaths,
  onNavigate,
  onToggleSelect,
}: FileListViewProps): React.ReactElement {
  const [sortKey, setSortKey] = useState<SortKey>('name');
  const [sortDir, setSortDir] = useState<SortDir>('asc');

  const handleSort = (key: SortKey) => {
    if (sortKey === key) {
      setSortDir(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortKey(key);
      setSortDir('asc');
    }
  };

  const sorted = useMemo(() => {
    const items = [...entries];
    const dir = sortDir === 'asc' ? 1 : -1;

    items.sort((a, b) => {
      // Directories always first
      if (a.isDirectory !== b.isDirectory) return a.isDirectory ? -1 : 1;

      switch (sortKey) {
        case 'name':
          return dir * a.name.localeCompare(b.name);
        case 'size': {
          const sizeA = a.isDirectory ? (dirSizes.get(a.path) || 0) : a.size;
          const sizeB = b.isDirectory ? (dirSizes.get(b.path) || 0) : b.size;
          return dir * (sizeA - sizeB);
        }
        case 'type': {
          const extA = a.isDirectory ? '' : getExtension(a.name);
          const extB = b.isDirectory ? '' : getExtension(b.name);
          return dir * extA.localeCompare(extB);
        }
        case 'mtime':
          return dir * (a.mtime || '').localeCompare(b.mtime || '');
        default:
          return 0;
      }
    });
    return items;
  }, [entries, dirSizes, sortKey, sortDir]);

  const sortIndicator = (key: SortKey) => {
    if (sortKey !== key) return '';
    return sortDir === 'asc' ? ' \u25B4' : ' \u25BE';
  };

  return (
    <div className="file-list">
      <table className="file-list__table">
        <thead>
          <tr>
            <th className="file-list__th file-list__th--check">&nbsp;</th>
            <th
              className="file-list__th file-list__th--name"
              onClick={() => handleSort('name')}
              role="button"
              tabIndex={0}
            >
              Name{sortIndicator('name')}
            </th>
            <th
              className="file-list__th file-list__th--size"
              onClick={() => handleSort('size')}
              role="button"
              tabIndex={0}
            >
              Size{sortIndicator('size')}
            </th>
            <th
              className="file-list__th file-list__th--type"
              onClick={() => handleSort('type')}
              role="button"
              tabIndex={0}
            >
              Type{sortIndicator('type')}
            </th>
            <th
              className="file-list__th file-list__th--mtime"
              onClick={() => handleSort('mtime')}
              role="button"
              tabIndex={0}
            >
              Modified{sortIndicator('mtime')}
            </th>
          </tr>
        </thead>
        <tbody>
          {sorted.map((entry) => {
            const isSelected = selectedPaths.has(entry.path);
            const size = entry.isDirectory
              ? (dirSizes.get(entry.path) || 0)
              : entry.size;

            return (
              <tr
                key={entry.path}
                className={`file-list__row ${isSelected ? 'file-list__row--selected' : ''} ${entry.isDirectory ? 'file-list__row--dir' : ''}`}
                onClick={() => {
                  if (entry.isDirectory) {
                    onNavigate(entry.path);
                  } else {
                    onToggleSelect(entry.path);
                  }
                }}
              >
                <td className="file-list__td file-list__td--check">
                  {!entry.isDirectory && (
                    <input
                      type="checkbox"
                      checked={isSelected}
                      onChange={(e) => {
                        e.stopPropagation();
                        onToggleSelect(entry.path);
                      }}
                      onClick={(e) => e.stopPropagation()}
                    />
                  )}
                </td>
                <td className="file-list__td file-list__td--name">
                  <span className="file-list__icon">
                    {entry.isDirectory ? '\uD83D\uDCC1' : '\uD83D\uDCC4'}
                  </span>
                  {entry.name}
                </td>
                <td className="file-list__td file-list__td--size">
                  {formatSize(size)}
                </td>
                <td className="file-list__td file-list__td--type">
                  {entry.isDirectory ? 'Folder' : (getExtension(entry.name) || '\u2014').toUpperCase()}
                </td>
                <td className="file-list__td file-list__td--mtime">
                  {entry.mtime ? new Date(entry.mtime).toLocaleDateString() : '\u2014'}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

export default FileListView;
